import { ListBullets } from "@phosphor-icons/react";

import {
  BooleanFilter,
  IncludeExcludeSelection,
  ListSelection,
  SelectionFilter,
  SelectionTags,
} from "common/components/Filter";
import SearchInputFilter from "common/components/Filter/SearchInputFilter";
import ProfileImage from "common/components/ui/ProfileImage";

import useNavigatorFilters from "../../../../../../hooks/useNavigatorFilters";
import CustomInputFilter from "./CustomInputFilter";
import SubfiltersFilter from "./SubfiltersFilter";
import TypeaheadValues from "./TypeaheadValues";

import type { NAVIGATOR_FILTER_CATEGORIES } from "../../../../../../constants";
import type {
  NavigatorFilters,
  SelectedNavigatorFilters,
} from "../../../../../../types";

export default function Filter({
  returnedFilters,
  selectedFilters,
  setSelectedFilters,
  filterData,
  isLoading,
}: {
  returnedFilters: NavigatorFilters;
  selectedFilters: SelectedNavigatorFilters;
  setSelectedFilters: (selectedFilters: SelectedNavigatorFilters) => void;
  filterData: (typeof NAVIGATOR_FILTER_CATEGORIES)[number]["filters"][number];
  isLoading: boolean;
}) {
  const { clearFilterType, updateFilterValue, setFilterValue } =
    useNavigatorFilters(selectedFilters, setSelectedFilters);

  const { label } = filterData;

  if ("subFilters" in filterData) {
    return (
      <SubfiltersFilter
        key={label}
        label={label}
        subFilters={filterData.subFilters}
        returnedFilters={returnedFilters}
        selectedFilters={selectedFilters}
        setSelectedFilters={setSelectedFilters}
      />
    );
  }

  const { selectionType, type } = filterData;

  // the value of the filter object returned from the server
  const returnedFilter = returnedFilters?.find(
    (filter) => filter.type === type,
  );
  // the value of the filter object sent to the server, if it's undefined the filter isn't selected
  const selectedFilter = selectedFilters.find((filter) => filter.type === type);

  const selectedValues = selectedFilter ? selectedFilter.values : [];
  const returnedValues = returnedFilter ? returnedFilter.values : [];

  if (selectionType === "boolean") {
    const { value } = filterData;
    let displayCount: string;
    if (returnedValues.length) {
      displayCount = returnedValues[0].displayCount;
    }
    return (
      <BooleanFilter
        key={type}
        isDisabled={displayCount === "0" || isLoading}
        label={`${label}${displayCount ? ` (${displayCount})` : ""}`}
        isChecked={!!selectedValues.length}
        onCheckedChange={(isChecked) => {
          if (isChecked) {
            updateFilterValue(type, "include", value);
          } else {
            clearFilterType(type);
          }
        }}
      />
    );
  }
  const renderTagsComponent = (resetInputValue?: () => void) => {
    if (selectedValues.length) {
      return (
        <SelectionTags
          selection={selectedValues}
          labelKey="text"
          onRemoveFilter={(value) => {
            updateFilterValue(type, "clear", value);
            if (resetInputValue) {
              resetInputValue();
            }
          }}
          onFlipFilter={
            selectionType === "includeExclude" &&
            ((value) => {
              updateFilterValue(
                type,
                value.selectionType === "INCLUDED" ? "exclude" : "include",
                value,
              );
            })
          }
        />
      );
    }
  };

  if (selectionType === "customValue") {
    return (
      <CustomInputFilter
        key={type}
        label={label}
        renderTagsComponent={renderTagsComponent}
        onAddValue={(customValue) => {
          setFilterValue(type, { id: customValue, text: customValue });
        }}
      />
    );
  }
  const { typeaheadType, isFixedTypeahead } = filterData;

  const renderSelectionComponent = (
    options: NavigatorFilters[number]["values"],
  ) => {
    if (selectionType === "includeExclude") {
      return (
        <IncludeExcludeSelection
          labelKey="text"
          countKey="displayCount"
          options={options}
          renderIcon={(option) => {
            const { logoUrl } = option;
            if (logoUrl) {
              return <ProfileImage src={logoUrl} size="sm" />;
            }
            if ("icon" in option) {
              return <ListBullets size={16} />;
            }
          }}
          onSelect={(updateType, selectedOption) =>
            updateFilterValue(type, updateType, selectedOption)
          }
        />
      );
    }
    return (
      <ListSelection
        countKey="displayCount"
        renderLabel={({ text }) => text}
        options={options}
        onSelect={(selectedOption) => {
          if (selectionType === "singleSelect") {
            setFilterValue(type, selectedOption);
          } else {
            updateFilterValue(type, "include", selectedOption);
          }
        }}
      />
    );
  };
  if (isFixedTypeahead) {
    return (
      <SelectionFilter
        key={type}
        label={label}
        tagsComponent={renderTagsComponent()}
      >
        <TypeaheadValues
          typeaheadType={typeaheadType}
          selectedValues={selectedValues}
          returnedValues={returnedValues}
          renderSelectionComponent={renderSelectionComponent}
        />
      </SelectionFilter>
    );
  }
  return (
    <SearchInputFilter
      key={type}
      label={label}
      renderTagsComponent={renderTagsComponent}
    >
      {(query) => (
        <TypeaheadValues
          query={query}
          typeaheadType={typeaheadType}
          selectedValues={selectedValues}
          returnedValues={returnedValues}
          renderSelectionComponent={renderSelectionComponent}
        />
      )}
    </SearchInputFilter>
  );
}
